/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import { Route, Routes, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import useScrollTop from 'core/hooks/useScrollTop';
import { SECTIONS } from 'constants/sections';
import { PasswordType } from './pages/auth/Password';
import { LoadingSpinner } from './components/Loading/LoadingSpinner';

// Code-split imports for current (v2) routes
const ProductPage = lazy(() => import('./pages/product/ProductPage'));
const LearnerPage = lazy(() => import('./pages/learner/LearnerPage'));
const ModuleSharePage = lazy(() => import('./pages/modules/Share/ModuleSharePage'));
const PagesV2 = lazy(() => import('./pages/Home'));
const ModulesPage = lazy(() => import('./pages/modules/ModulesPage'));
const CoursePage = lazy(() => import('./pages/course/CoursePage'));
const AdminBoundary = lazy(() => import('./features/AdminBoundary/AdminBoundary'));
const WorkFlowPage = lazy(() => import('./pages/course/WorkFlow/WorkFlowPage'));
const CourseLearnersPage = lazy(() => import('./pages/course/Learners/CourseLearners'));
const InviteCourseLearners = lazy(() => import('./pages/course/Invites/InviteCourseLearners'));
const InviteCourseLearnerRemindersPage = lazy(() => import('./pages/course/Invites/InviteReminders'));
const Integrations = lazy(() => import('./pages/course/Integrations/CourseIntegrations'));
const IntegrationEdit = lazy(() => import('./pages/course/Integrations/Edit/CourseIntegrationEdit'));
const Certificates = lazy(() => import('./pages/course/Certificates/Certificates'));
const CourseAdmins = lazy(() => import('./pages/course/Admins/CourseAdmins'));
const InviteCourseAdmins = lazy(() => import('./pages/course/Admins/InviteCourseAdmins'));
const NewModulePage = lazy(() => import('./pages/modules/New/NewModulePage'));
const NewModuleTypePage = lazy(() => import('./pages/modules/New/Type/NewModuleTypePage'));
const ModuleImportPage = lazy(() => import('./pages/modules/New/Import/ModuleImportPage'));
const LearnerProgressPage = lazy(() => import('./pages/course/Learner/LearnerProgressPage'));
const LearnerEmailPage = lazy(() => import('./pages/learner/Email/LearnerEmailPage'));
const LearnerVariablePage = lazy(() => import('./pages/learner/Variable/LearnerVariablePage'));
const CoursesPage = lazy(() => import('./pages/courses/CoursesPage'));
const NewCoursePage = lazy(() => import('./pages/courses/New/NewCoursePage'));
const ModuleViewPage = lazy(() => import('./pages/modules/View/ModuleViewPage'));
const ModuleEditPage = lazy(() => import('./pages/modules/Edit/ModuleEditPage'));
const ThemePage = lazy(() => import('./pages/settings/theme/ThemePage'));
const DesignSystemPage = lazy(() => import('./pages/settings/design-system/DesignSystemPage'));
const LearnersPage = lazy(() => import('./pages/learners/LearnersPage'));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const PlatformAdminsPage = lazy(() => import('./pages/admins/PlatformAdminsPage'));
const InvitePlatformAdmin = lazy(() => import('./pages/admins/InvitePlatformAdmin'));
const VariableReportPage = lazy(() => import('./pages/course/Integrations/Report/VariableReportPage'));
const CourseNodePreviewPage = lazy(() => import('./pages/course/CourseNode/CourseNodePage'));

// Code-split imports for auth/public routes
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const LogIn = lazy(() => import('./pages/auth/LogIn').then(m => ({ default: m.LogIn })));
const Password = lazy(() => import('./pages/auth/Password').then(m => ({ default: m.Password })));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const SignUp = lazy(() => import('./pages/auth/SignUp').then(m => ({ default: m.SignUp })));
const New = lazy(() => import('./pages/auth/New').then(m => ({ default: m.New })));

const router = createBrowserRouter([{ path: '*', Component: Root }]);

export function App() {
  return (
    <>
      <RouterProvider router={router} />
      <video id="videoEl" className="hide-video" playsInline muted webkit-playsinline="true" />
    </>
  );
}

export function Root() {
  useTranslation();
  useScrollTop();
  return (
    <Suspense
      fallback={
        <div className="w-full h-full flex justify-center items-center">
          <LoadingSpinner />
        </div>
      }
    >
      <Routes>
        <Route path={`en/:token`} element={<ProductPage />} />
        <Route path={`scorm/:lang/:token`} element={<ProductPage scorm={true} />} />
        <Route path={`en/:token/:entityToken`} element={<ProductPage />} />
        <Route path={`invite/:inviteToken`} element={<ProductPage />} />
        <Route path={`sso/:inviteToken/:token`} element={<ProductPage />} />
        <Route path={`invite/:inviteToken/:entityToken`} element={<ProductPage />} />
        <Route path={`token/:token/:entityToken`} element={<ProductPage />} />
        {/* Current version routes (modern pages) */}
        <Route path={'/'} element={<AdminBoundary />}>
          <Route index={true} element={<PagesV2 />} />
          <Route path={SECTIONS.MODULES}>
            <Route index={true} element={<ModulesPage isHome />} />
            <Route path={`new`} element={<NewModulePage />} />
            <Route path={`new/import`} element={<ModuleImportPage />} />
            <Route path={`new/:type`} element={<NewModuleTypePage />} />
          </Route>
          <Route path={SECTIONS.MODULE}></Route>
          <Route path={SECTIONS.COURSES} element={<CoursesPage />} />
          <Route path={`${SECTIONS.COURSE}/new`} element={<NewCoursePage />} />
          <Route path={SECTIONS.LEARNERS} element={<LearnersPage />} />
          <Route path={SECTIONS.VARIABLES} element={<ReportsPage />} />
          <Route path={`${SECTIONS.LEARNER}/:userId`} element={<LearnerPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId`} element={<CoursePage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}`} element={<ModulesPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/new`} element={<NewModulePage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/new/import`} element={<ModuleImportPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/new/:type`} element={<NewModuleTypePage />} />

          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.COURSE_NODE}/:courseNodeId/share`}
            element={<ModuleSharePage />}
          />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.COURSE_NODE}/:courseNodeId/preview`}
            element={<CourseNodePreviewPage />}
          />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.COURSE_NODE}/:courseNodeId/:mode`}
            element={<CourseNodePreviewPage />}
          />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.MODULES}/:moduleId/preview`}
            element={<ModuleViewPage />}
          />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.WORKFLOW}`} element={<WorkFlowPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.LEARNERS}`} element={<CourseLearnersPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INVITES}`} element={<CourseLearnersPage />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INVITES}/new`} element={<InviteCourseLearners />} />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INVITES}/reminders`}
            element={<InviteCourseLearnerRemindersPage />}
          />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.LEARNERS}/progress/:courseProgressId`}>
            <Route index={true} element={<LearnerProgressPage />} />
            <Route path={`email/:emailId`} element={<LearnerEmailPage />}></Route>
            <Route path={`variable/:orgUserVariableId`} element={<LearnerVariablePage />}></Route>
          </Route>
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INTEGRATIONS}`} element={<Integrations />} />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.INTEGRATIONS}/:orgSystemId/edit`}
            element={<IntegrationEdit />}
          />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.VARIABLES}/:variableId`}
            element={<VariableReportPage />}
          />
          <Route path={`${SECTIONS.COURSE}/:courseId/certificates`} element={<Certificates />} />
          <Route path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.ADMINISTRATORS}`} element={<CourseAdmins />} />
          <Route
            path={`${SECTIONS.COURSE}/:courseId/${SECTIONS.ADMINISTRATORS}/invite`}
            element={<InviteCourseAdmins />}
          />
          <Route path={SECTIONS.THEME} element={<ThemePage />} />
          <Route path="design-system" element={<DesignSystemPage />} />
          <Route path={SECTIONS.ADMINISTRATORS} element={<PlatformAdminsPage />} />
          <Route path={`${SECTIONS.ADMINISTRATORS}/invite`} element={<InvitePlatformAdmin />} />
        </Route>

        {/* Public routes and special routes that don't need versioning */}
        <Route path={'manager'} element={<LogIn />} />

        {/* Authentication routes */}
        <Route path={`hello/:token`} element={<Password type={PasswordType.RESET} />} />
        <Route path={`hello/:token/:returnToken`} element={<Password type={PasswordType.RESET} />} />
        <Route path={`reset/:token`} element={<Password type={PasswordType.RESET} />} />
        <Route path={`forgot`} element={<ForgotPassword />} />
        <Route path={`signup`} element={<SignUp />} />
        <Route path={'new/:token'} element={<New />} />
        <Route path={'new/:token/:courseId/:moduleId'} element={<New />} />

        {/* 404 fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
